// ✅ src/pages/VehicleV2/JobCardEntryDetailReportAllPrint.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/jobCardEntryReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};
const toStr = (v) => String(v ?? "").trim();

export default function JobCardEntryDetailReportAllPrint() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const page = toInt(qs.get("page"), 1);
  const pageSize = toInt(qs.get("pageSize"), 1000); // print normally wants all

  // ✅ map backend row -> screenshot table fields
  const mapRow = (r, idx) => {
    const sNo = (page - 1) * pageSize + idx + 1;

    return {
      sNo,

      vehicleMainType: r.fld_vehicletypename ?? r.vehicleMainType ?? "",
      jobCardNo: r.fld_jobcardno ?? r.jobCardNo ?? "",
      entryDate: r.fld_entrydate ?? r.entryDate ?? "",
      vehicleName: r.fld_vehicle_name ?? r.vehicleName ?? "",
      vehicleRegNo: r.fld_vehicle_regno ?? r.vehicleRegNo ?? "",
      location: r.fld_branchname ?? r.location ?? r.fld_location ?? "",

      // screenshot shows Hr/Km
      hrKm:
        r.fld_hrkm ??
        r.fld_workinghrs ??
        r.fld_workingkm ??
        r.fld_hrs ??
        r.hrKm ??
        "",

      status: r.fld_status ?? r.status ?? "",
      complaint: r.fld_natureofcomplaint ?? r.complaint ?? "",
      attendedBy: r.fld_complaintattendedby ?? r.attendedBy ?? "",
      inspectedBy: r.fld_inspectedby ?? r.inspectedBy ?? "",
      remedy: r.fld_remedy ?? r.remedy ?? "",

      fromDate: r.fld_fromdate ?? r.fromDate ?? "",
      inTime: r.fld_intime ?? r.inTime ?? "",
      toDate: r.fld_todate ?? r.toDate ?? "",
      outTime: r.fld_outtime ?? r.outTime ?? "",

      // screenshot shows Hrs (separate col)
      hrs:
        r.fld_hours ??
        r.fld_hrs ??
        r.fld_maintainhrs ??
        r.fld_complainthrs ??
        r.hrs ??
        "",

      sparesDetails: r.fld_sparedetails ?? r.sparesDetails ?? "",
      maintenanceBy: r.fld_maintenanceby ?? r.maintenanceBy ?? "",
    };
  };

  const fetchPrintData = async () => {
    try {
      setLoading(true);

      // send all URL query params to backend list API
      const params = Object.fromEntries(qs.entries());

      const res = await axios.get(`${API_BASE}/list`, { params });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed");

      const dataRows = (res.data.rows || []).map(mapRow);
      setRows(dataRows);
    } catch (e) {
      setRows([]);
      alert(e?.message || "Failed to load print data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden in print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors"
          disabled={loading}
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="max-w-7xl mx-auto border-2 border-black p-3 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header */}
          <div className="border border-black p-3 mb-3 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-[11px] leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-3">
            Job Card Entry Detail Report
          </div>

          {/* Table (MATCH SCREENSHOT) */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-[10px]">
              <thead>
                <tr>
                  <th className="border border-black p-1 text-center font-bold">S. No</th>
                  <th className="border border-black p-1 text-center font-bold">Vehicle Main Type</th>
                  <th className="border border-black p-1 text-center font-bold">Job Card No</th>
                  <th className="border border-black p-1 text-center font-bold">Entry Date</th>
                  <th className="border border-black p-1 text-center font-bold">Vehicle Name</th>
                  <th className="border border-black p-1 text-center font-bold">Vehicle Reg No</th>
                  <th className="border border-black p-1 text-center font-bold">Location</th>
                  <th className="border border-black p-1 text-center font-bold">Hr/Km</th>
                  <th className="border border-black p-1 text-center font-bold">Status</th>
                  <th className="border border-black p-1 text-center font-bold">Nature Of Complaint</th>
                  <th className="border border-black p-1 text-center font-bold">Complaint Attended By</th>
                  <th className="border border-black p-1 text-center font-bold">Inspected By</th>
                  <th className="border border-black p-1 text-center font-bold">Remedy</th>
                  <th className="border border-black p-1 text-center font-bold">From Date</th>
                  <th className="border border-black p-1 text-center font-bold">In Time</th>
                  <th className="border border-black p-1 text-center font-bold">To Date</th>
                  <th className="border border-black p-1 text-center font-bold">Out Time</th>
                  <th className="border border-black p-1 text-center font-bold">Hrs</th>
                  <th className="border border-black p-1 text-center font-bold">Spares Details</th>
                  <th className="border border-black p-1 text-center font-bold">Maintenance By</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((x) => (
                  <tr key={x.sNo}>
                    <td className="border border-black p-1 text-center">{x.sNo}</td>
                    <td className="border border-black p-1 text-center">{x.vehicleMainType}</td>
                    <td className="border border-black p-1 text-center">{x.jobCardNo}</td>
                    <td className="border border-black p-1 text-center">{x.entryDate}</td>
                    <td className="border border-black p-1 text-center">{x.vehicleName}</td>
                    <td className="border border-black p-1 text-center">{x.vehicleRegNo}</td>
                    <td className="border border-black p-1 text-center">{x.location}</td>
                    <td className="border border-black p-1 text-center">{x.hrKm}</td>
                    <td className="border border-black p-1 text-center">{x.status}</td>
                    <td className="border border-black p-1 text-center">{x.complaint}</td>
                    <td className="border border-black p-1 text-center">{x.attendedBy}</td>
                    <td className="border border-black p-1 text-center">{x.inspectedBy}</td>
                    <td className="border border-black p-1 text-center">{x.remedy}</td>
                    <td className="border border-black p-1 text-center">{x.fromDate}</td>
                    <td className="border border-black p-1 text-center">{x.inTime}</td>
                    <td className="border border-black p-1 text-center">{x.toDate}</td>
                    <td className="border border-black p-1 text-center">{x.outTime}</td>
                    <td className="border border-black p-1 text-center">{x.hrs}</td>
                    <td className="border border-black p-1 text-center">{x.sparesDetails}</td>
                    <td className="border border-black p-1 text-center">{x.maintenanceBy}</td>
                  </tr>
                ))}

                {rows.length === 0 && !loading && (
                  <tr>
                    <td className="border border-black p-2 text-center" colSpan={20}>
                      No records
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 p-2 border border-black bg-gray-100 text-[11px]">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm;
          }
          body {
            margin: 0;
            padding: 0;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          .print\\:mx-0 {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .print\\:border-2 {
            border-width: 2px !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}
